import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import LabeledCheckbox from '../inputs/LabeledCheckBox';
import { FaTimes } from "react-icons/fa";
import { modifyMember } from '../../api/admin/AdminDashboardAPI';
import _ from 'lodash';
import toast from 'react-hot-toast';
import NumberInput from '../inputs/NumberInput';
import InputContainer from './adminSettings/InputContainer';

Modal.setAppElement('#root');

const MemberEditModal = ({
    isOpen,
    onClose,
    member,
    initialFormData,
    onFormDataChange,
    updateMember
}) => {
    // Initialize form state with defaults
    const originalOverride = initialFormData?.isOverrideStatusEnabled
        ?? member.activeOverride
        ?? false;

    const [name, setName] = useState(member.firstName + " " + member.lastName);
    const [isAdmin, setIsAdmin] = useState(initialFormData?.adminStatus || member?.adminStatus || false)
    const [status, setStatus] = useState("");
    const [isOverrideStatusEnabled, setIsOverrideStatusEnabled] = useState(initialFormData?.isOverrideStatusEnabled || member?.activeOverride || false);
    const [isOverrideGeneralMeetingEnabled, setIsOverrideGeneralMeetingEnabled] = useState(initialFormData?.isOverrideGeneralMeetingEnabled || member?.generalMeetingOverride || false);
    //If manual semester count < active count set to active count
    const [manualSemesterCount, setManualSemesterCount] = useState(initialFormData?.manualSemesterCount || (member?.manualSemesterCount >= member?.activeMemberSemestersCount ? member?.manualSemesterCount : member?.activeMemberSemestersCount) || 0);

    const baseCount = member.activeMemberSemestersCount || 0;

    const delta =
        isOverrideStatusEnabled !== originalOverride
            ? (isOverrideStatusEnabled ? 1 : -1)
            : 0;

    const floor = baseCount + delta;


    // Ref to ensure we only initialize once per modal open
    const didInit = useRef(false);
    // Ref to hold a stable version of onFormDataChange
    const onFormDataChangeRef = useRef(onFormDataChange);

    // Update the ref if onFormDataChange changes
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    // Initialization effect – runs when modal opens
    useEffect(() => {
        if (isOpen) {
            if (!didInit.current) {
                if (initialFormData && Object.keys(initialFormData).length > 0) {
                    setStatus(initialFormData.status || "");
                    setIsOverrideStatusEnabled(initialFormData.isOverrideStatusEnabled);
                    setIsOverrideGeneralMeetingEnabled(initialFormData.isOverrideGeneralMeetingEnabled);
                    setIsAdmin(initialFormData.adminStatus)
                    setManualSemesterCount(initialFormData.manualSemesterCount);
                } else if (member) {
                    setStatus(member.status || "");
                    setIsOverrideStatusEnabled(member.activeOverride ?? false);
                    setIsOverrideGeneralMeetingEnabled(member.generalMeetingOverride ?? false);
                    setIsAdmin(member.adminStatus)
                    setManualSemesterCount(member.manualSemesterCount || (member?.manualSemesterCount >= member?.activeMemberSemestersCount ? member?.manualSemesterCount : member?.activeMemberSemestersCount) || 0);
                }
                didInit.current = true;
            }
        } else {
            // Reset the flag when modal closes
            didInit.current = false;
        }
    }, [isOpen, initialFormData, member]);

    // Propagate form data to parent without including the unstable callback in the deps
    useEffect(() => {
        onFormDataChangeRef.current &&
            onFormDataChangeRef.current({
                status,
                isOverrideStatusEnabled,
                isOverrideGeneralMeetingEnabled,
                manualSemesterCount,
                isAdmin
            });
    }, [
        status,
        isOverrideStatusEnabled,
        isOverrideGeneralMeetingEnabled,
        manualSemesterCount,
        isAdmin
    ]);

    const handleOverrideStatusChange = e => {
        const checked = e.target.checked;
        setIsOverrideStatusEnabled(checked);

        setManualSemesterCount(curr =>
            checked
                ? curr + 1
                : curr - 1
        );
    };

    const handleOverrideGeneralMeetingChange = (event) => {
        setIsOverrideGeneralMeetingEnabled(event.target.checked);
    };

    const handleIsAdminChange = (event) => {
        setIsAdmin(event.target.checked);
    };

    const handleManualInput = e => {
        const val = Number(e.target.value) || 0;
        const floor = baseCount + (isOverrideStatusEnabled ? 1 : 0);
        setManualSemesterCount(Math.max(val, floor));
    };

    const handleReset = () => {
        if (member) {
            const defaultName = member.firstName + " " + member.lastName;
            const defaultStatus = member.status || "";
            const defaultOverrideStatus = member.activeOverride ?? false;
            const defaultOverrideGeneralMeeting = member.generalMeetingOverride ?? false;
            const defaultManualSemesterCount = (member?.manualSemesterCount >= member?.activeMemberSemestersCount ? member?.manualSemesterCount : member?.activeMemberSemestersCount) || 0;
            const defaultAdminStatus = member?.adminStatus ?? false;

            setName(defaultName);
            setStatus(defaultStatus);
            setIsOverrideStatusEnabled(defaultOverrideStatus);
            setIsOverrideGeneralMeetingEnabled(defaultOverrideGeneralMeeting);
            setManualSemesterCount(defaultManualSemesterCount);
            setIsAdmin(defaultAdminStatus)
        }
    };

    const submitMember = async () => {
        const oldMember = {
            id: member.id,
            activeOverride: member.activeOverride,
            generalMeetingOverride: member.generalMeetingOverride,
            manualSemesterCount: member.manualSemesterCount,
            adminStatus: member.adminStatus
        }

        const updatedMember = {
            id: member.id,
            activeOverride: isOverrideStatusEnabled,
            generalMeetingOverride: isOverrideGeneralMeetingEnabled,
            manualSemesterCount: manualSemesterCount,
            adminStatus: isAdmin
        };

        const isUnchanged = _.isEqual(oldMember, updatedMember);

        if (isUnchanged) {
            toast.error("No data changed");
            return;
        }

        try {
            toast.loading("Updating Member...");
            setTimeout(async () => {
                console.log("Payload to update:", updatedMember);
                const response = await modifyMember(updatedMember);
                if (response.status === 200 || response.status === 201) {
                    toast.dismiss();
                    didInit.current = false;
                    updateMember(updatedMember);
                    toast.success("Member updated!");
                    onClose();
                } else {
                    toast.dismiss();
                    console.log("Error updating member. Status:", response.status);
                    toast.error("Member failed to update. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting member:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Member Edit Modal"
            className="custom-modal-content"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className='d-flex flex-row justify-content-between align-items-center mb-3'>
                    <h2 className="text-start mb-0">Edit Member: {name}</h2>
                    <div className='d-flex flex-row justify-content-end align-items-center me-1'>
                        <button className="close-btn" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <div className="d-flex flex-row mb-2 fs-5">
                    <div className='me-2'>Status:</div>
                    <div className={status === "Active" ? "text-success" : "text-danger"}>{status}</div>
                </div>
                <div className="d-flex justify-content-between mb-0">
                    <span className='input-label fs-5'>Report CO-OP / Class Conflict</span>
                </div>
                <div className="d-flex justify-content-between ms-3 mb-0">
                    <LabeledCheckbox
                        id="override-status-checkbox"
                        label="Override Status:"
                        checked={isOverrideStatusEnabled}
                        onChange={handleOverrideStatusChange}
                        disabled={false}
                    />
                </div>
                <div className="d-flex justify-content-between ms-3 mb-2">
                    <LabeledCheckbox
                        id="override-general-meeting-checkbox"
                        label="Override General Meeting Requirement:"
                        checked={isOverrideGeneralMeetingEnabled}
                        onChange={handleOverrideGeneralMeetingChange}
                        disabled={false}
                    />
                </div>
                <div className="d-flex justify-content-between ms-3 mb-2">
                    <LabeledCheckbox
                        id="admin-role"
                        label="IS ADMIN?"
                        checked={isAdmin}
                        onChange={handleIsAdminChange}
                        disabled={false}
                    />
                </div>
                <InputContainer>
                    <NumberInput
                        id="requirement-threshold"
                        label='Total Semester Count'
                        labelClassName="fs-5"
                        min={floor}
                        value={manualSemesterCount}
                        setValue={handleManualInput}
                    />
                </InputContainer>
                <div className='d-flex flex-row justify-content-center align-items-center two-button-container'>
                    <div className='me-3'>
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Save"
                        action={submitMember}
                    />
                </div>
            </>
        </Modal>
    );
};

MemberEditModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    member: PropTypes.object,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
};

export default MemberEditModal;
